/**
 * Tasks and departments — the Phase 1 Tasks page (`chorus-byggeplan.md` Fase 1.2;
 * `docs/design/00-contracts.md` §6.12, §6.16, §12.5, §12.6).
 *
 * Two families of DTO, one page:
 *
 * * **Task** — a piece of work. `edition_id IS NULL` is a personal / organization-level task, so
 *   the global "alle mine oppgaver på tvers av festivaler" view is the natural list and an
 *   edition filter is the narrowing, not the other way round.
 * * **Department** — "gruppe/avdeling", which the plan is explicit about: *data, ikke en enum*
 *   (principle 9). They live here rather than in a taxonomy of their own for the same reason
 *   tags live in `contacts.ts`: this is the page that groups by them, and the page that gets
 *   "enkel administrasjon her i første omgang" until Settings arrives in Phase 2.
 *
 * ---------------------------------------------------------------------------------------------
 * GROUPING IS THE SORT ORDER, AND THAT IS WHY IT IS A SERVER CONCERN
 *
 * The plan asks for "alle mine oppgaver på tvers av festivaler, gruppert (etter frist, festival
 * eller gruppe)". A client that groups only the page it is holding is telling the same lie as a
 * client that sorts the page it is holding: the second page brings rows that belong in a group
 * the reader has already scrolled past, and the group headers are then a summary of an arbitrary
 * fifty rows rather than of the list.
 *
 * So grouping happens where the whole set is: in the `ORDER BY`. There is deliberately no
 * `groupBy` parameter beside `sort`, because two parameters that must agree are two parameters
 * that can disagree — a `sort=created&groupBy=department` would be a request for a grouping the
 * database cannot deliver adjacently. Instead the grouping IS the sort key:
 *
 * * `due`        — deadline order. Tasks sharing a day are adjacent, so "gruppert etter frist"
 *                  is the client bucketing `dueAt` by date. Undated work sorts last, always.
 * * `edition`    — edition first, deadline within it. "Gruppert etter festival": every item
 *                  carries its `edition`, and the edition carries its festival, so the client
 *                  renders "God Nok 2027" headers with no second request. Personal and
 *                  organization-level tasks (`edition = null`) form the first group.
 * * `department` — department first, deadline within it. Ungrouped work forms the first group.
 * * `created`    — newest first with `direction=desc`; no grouping, and the plain feed order.
 *
 * What stays a client concern is only the *rendering* of a header when the group key changes
 * between two adjacent rows — which is exactly the part that needs no knowledge of the rest of
 * the set.
 * ---------------------------------------------------------------------------------------------
 *
 * Three contract decisions worth stating out loud, because each one is forced rather than
 * chosen:
 *
 * 1. **`status` is absent from `UpdateTaskRequest`.** Completing a task is `task:complete` in the
 *    role matrix and updating it is `task:update`; a `PATCH` that accepted `status` would let a
 *    `limited` creator close work only the assignee may close. Status moves through
 *    `POST /v1/tasks/:id/status` and nowhere else.
 * 2. **The assignee is a `personId`, not a `contactId`.** `tasks.assignee_person_id` has a
 *    composite foreign key to `contacts (organization_id, person_id)`, so the assignee is
 *    already "chosen from Contacts" as the plan requires — and the id that survives is the
 *    person's, which is the one that means the same thing in every organization.
 * 3. **`dueAt` is a timestamp, not a date.** Get-in is at 07:00 and curfew is at 23:00; a
 *    festival deadline that loses its time of day loses the half of it that matters.
 */
import { z } from 'zod';

import { taskPrioritySchema, taskStatusSchema, TASK_PRIORITIES, TASK_STATUSES } from '../enums.js';
import { cursorQuerySchema, page, pageCursorFields } from '../http/pagination.js';
import { ACCREDITATION_COLOURS } from './accreditation.js';
/*
 * «Organisasjonen» som en verdi i arrangementlista (#39). Den bor i kalenderens kontrakt fordi den
 * ble skrevet der først, og den IMPORTERES hit framfor å bli skrevet om: to konstanter med samme
 * ord ville vært to ord den dagen den ene ble rettet.
 */
import { ORGANISATION_ONLY } from './calendar.js';

/** A repeated (`?status=todo&status=blocked`) or comma-separated (`?status=todo,blocked`) list. */
const statusListQuerySchema = z.preprocess((value) => {
  if (value === undefined || value === null) return undefined;
  if (Array.isArray(value)) return value;
  const text = String(value).trim();
  return text.length === 0 ? undefined : text.split(',');
}, z.array(taskStatusSchema).min(1).max(TASK_STATUSES.length));

/** The same shape for `?priority=high,urgent`, which the filter row offers as chips. */
const priorityListQuerySchema = z.preprocess((value) => {
  if (value === undefined || value === null) return undefined;
  if (Array.isArray(value)) return value;
  const text = String(value).trim();
  return text.length === 0 ? undefined : text.split(',');
}, z.array(taskPrioritySchema).min(1).max(TASK_PRIORITIES.length));

/** `?overdue=true`. Absent and `false` mean the same thing: do not narrow the list. */
const overdueQuerySchema = z.preprocess(
  (value) => (value === undefined || value === null ? undefined : String(value) === 'true'),
  z.boolean(),
);

/** `?editionIds=a,b,organisation` — se `listTasksQuerySchema.editionIds`. Delt med tidslinja. */
const editionIdsQuerySchema = z.preprocess(
  (value) => {
    if (value === undefined || value === null) return undefined;
    if (Array.isArray(value)) return value;
    const text = String(value).trim();
    return text.length === 0 ? undefined : text.split(',');
  },
  z
    .array(z.union([z.uuid(), z.literal(ORGANISATION_ONLY)]))
    .min(1)
    .max(50)
    .optional(),
);

const titleSchema = z.string().trim().min(1).max(200);
/** Markdown. Long enough for a checklist and an advance note, short enough not to be a document. */
const descriptionSchema = z.string().max(20_000);

/**
 * EN RRULE ER EN STRENG HER OG EN REGEL I TJENESTEN (avgjørelse 5).
 *
 * Zod sjekker formen — at den begynner med `FREQ=` og holder seg innenfor tegnene RFC 5545 bruker
 * — og `rrule`-pakken i API-et er den som avgjør om den lar seg tolke. To grunner til at
 * kontrakten ikke gjør hele jobben: pakken er en avhengighet `packages/shared` ikke har (den må
 * kunne kjøre i en nettleser uten den), og en regel som er syntaktisk gyldig og semantisk
 * meningsløs — `FREQ=DAILY;COUNT=0` — avvises av tjenesten uansett.
 *
 * `DTSTART` står IKKE i strengen. Forekomstene regnes fra oppgavens egen `due_at`, så en regel som
 * bar sin egen startdato ville hatt to sannheter om når den begynner.
 */
const rruleSchema = z
  .string()
  .trim()
  .min(6)
  .max(500)
  .regex(/^FREQ=[A-Z0-9;=,+\-:]*$/u, 'It must be an RFC 5545 RRULE beginning with FREQ=.');

/**
 * Hvor mange etiketter én oppgave får bære.
 *
 * Tolv, og tallet er en lesbarhetsgrense framfor en teknisk: en rad i lista har plass til tre-fire
 * chips før tittelen blir borte, og en oppgave med tolv etiketter har ingen. Grensa er der for at
 * en klient ikke skal kunne skrive femti lenkerader i ett kall.
 */
export const MAX_TASK_LABELS_PER_TASK = 12;

/**
 * The sort keys `GET /v1/tasks` offers — which are the groupings, see the file header. The first
 * is the default, and every one of them is backed by an index that already orders exactly what
 * the keyset cursor compares (migration 0009):
 *
 * * `due`        → `ix_tasks__org_due_key`            (organization_id, dueKey, id)
 *                  or `ix_tasks__org_assignee_due_key` / `ix_tasks__org_creator_due_key` once the
 *                  list is narrowed to one person — which the global "my tasks" view always is
 * * `edition`    → `ix_tasks__org_edition_due_key`    (organization_id, editionKey, dueKey, id)
 * * `department` → `ix_tasks__org_department_due_key` (organization_id, departmentKey, dueKey, id)
 * * `created`    → `ux_tasks__org_id` / `ix_tasks__org_assignee_recent`, both (organization_id, …, id)
 *                  — ids are UUIDv7, so `id` **is** creation order and needs no second column
 *
 * There is deliberately no `title`, `status` or `priority` sort. `priority` has no index at all,
 * and a four-value column at the head of an `ORDER BY` is the migration-0008 mistake in its
 * purest form: every page would be one enormous tie broken by an invisible UUID. Adding a sort
 * starts with a migration, not with a string in this array.
 */
export const TASK_SORT_KEYS = ['due', 'edition', 'department', 'created'] as const;
export type TaskSortKey = (typeof TASK_SORT_KEYS)[number];

/** The one sort `GET /v1/departments` offers: the manual order the organization gave them. */
export const DEPARTMENT_SORT_KEYS = ['order'] as const;
export type DepartmentSortKey = (typeof DEPARTMENT_SORT_KEYS)[number];

// ---------------------------------------------------------------------------- departments
export const departmentDtoSchema = z
  .object({
    id: z.uuid(),
    /** Stable slug generated from the name; the seed and the Phase 2 edition clone use it. */
    key: z.string(),
    name: z.string(),
    /*
     * NO `color`. Atle, 28 August, answering question 9: the colour is DERIVED, not chosen.
     *
     * The column existed, the seed filled it, the DTO carried it to the browser — and nothing
     * read it. `TasksList` and `TaskDetailPage` both draw a department with
     * `categoricalColorVar(department.id)`, a token from the categorical palette with a value per
     * colour mode. It surfaced when the departments page was built: it was supposed to have a
     * colour picker, and a picker that writes to a field nobody reads is worse than none.
     *
     * Deriving is also the only one of the two options that cannot produce an unreadable colour:
     * a free hex chosen in light mode disappears in dark, and the same group would have shown two
     * different colours on two pages for as long as both mechanisms existed.
     */
    /** Manual ordering. The list is sorted by it, then by name. */
    sortOrder: z.number().int(),
    /** A *section* is a department with a parent. Two levels, never a third (§6.12). */
    parentDepartmentId: z.uuid().nullable(),
    /**
     * AVDELINGSLEDERNE (K-4), som navn og ikke bare som id-er.
     *
     * Atle, 19. september: «Avdelinger på årgangen kan få én eller flere ledere (personer i
     * organisasjonen). Lederen vises i avdelingstreet og i detaljpanelet. Rettigheter og varsler
     * knyttes IKKE til rollen nå.»
     *
     * Navnet ligger i svaret framfor at treet slår det opp selv: lista tegner ni avdelinger med
     * hver sin leder, og en id per rad ville vært ni oppslag mot kontaktlista for å skrive ni
     * navn — N+1 i sin reneste form, på en liste som allerede leses i én spørring.
     *
     * Raden finnes fra før: `department_members.is_lead` har ligget i skjemaet siden 0001 og har
     * aldri vært eksponert. K-4 trengte derfor ingen migrering, bare en vei ut.
     */
    leads: z.array(
      z.object({
        personId: z.uuid(),
        /** Null når kontaktraden er mykslettet etter at lederen ble satt. */
        displayName: z.string().nullable(),
      }),
    ),
    /**
     * MEDLEMMENE SOM IKKE LEDER (239.33/F33, Atle 04.10): «egen avdeling» betyr «med i», og
     * medlemskapet står per årgang i årgangens tre. En leder står bare i `leads`.
     */
    members: z.array(
      z.object({
        personId: z.uuid(),
        displayName: z.string().nullable(),
      }),
    ),
  })
  .meta({ id: 'Department' });

export const listDepartmentsQuerySchema = cursorQuerySchema(DEPARTMENT_SORT_KEYS)
  .extend({
    /**
     * WHICH TREE. ABSENT MEANS THE ORGANISATION'S OWN, WHICH IS WHAT SETTINGS ADMINISTERS.
     *
     * `departments` holds two kinds of row at once: the organisation's template (`edition_id is
     * null`) and each edition's own copy of it. `ux_departments__scope_key` is
     * `(organization_id, edition_id, key)` with `nullsNotDistinct`, so both coexist and `key` is
     * what links a department to its copy — that is the mechanism the edition clone uses.
     *
     * Until now the list could only ever answer with the template: `DepartmentsService` had
     * `edition_id is null` written into the query. That was right while nothing else existed, and
     * it stopped being right the moment a clone could write nine departments into an edition that
     * no surface in the product could then show. The receipt was the only evidence the clone had
     * happened.
     *
     * ABSENT rather than a `'organization' | uuid` union, deliberately, and this is the one place
     * an implicit default earns itself: every existing caller — the Settings page, the task
     * filter, the resolver — means the template, and none of them should have to say so. A new
     * parameter that changed what an old call returns is the kind of silent behaviour change no
     * test written before it would catch.
     */
    editionId: z.uuid().optional(),
  })
  .meta({
    id: 'ListDepartmentsQuery',
  });

export const departmentListResponseSchema = page(departmentDtoSchema, 'DepartmentListResponse');

export const createDepartmentRequestSchema = z
  .object({
    /**
     * HVILKET TRE — PÅKREVD, OG `null` BETYR MALEN MED VILJE (avgjørelse 4 og trinn 0).
     *
     * Lista over (`listDepartmentsQuerySchema.editionId`) er VALGFRI og lar fravær bety malen,
     * fordi hver eksisterende leser mener malen og ingen av dem burde måtte si det. Å skrive er
     * ikke det samme: fra og med trinn 0 er årgangens tre det treet personer, skift, poster og
     * ressurser peker på, og malen er bare startpunktet for den første årgangen.
     *
     * Et kall som GLEMMER feltet ville da skrevet stille i malen i stedet for i årgangen — en rad
     * på feil nivå som ingenting sier fra om, og som først blir synlig når noen ser etter en
     * avdeling i årets tre og ikke finner den. Det er nøyaktig den feilen
     * `edition-departments.ts` allerede advarer mot på leses-siden: «A page that dropped
     * `editionId` would not fail. It would render ... and nothing on screen would say so.»
     *
     * Så: påkrevd, og `null` er en påstand framfor en forglemmelse. Samme form som
     * `cloneSourceSchema` valgte av samme grunn — «a request that forgot to send the id would
     * silently get the wrong source instead of a 400».
     */
    editionId: z.uuid().nullable(),
    name: z.string().trim().min(1).max(80),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    /**
     * Makes this a *section* of another department. The depth cap of two levels is enforced by
     * the service, not the database (§6.12), so a parent that already has a parent is refused.
     *
     * Forelderen må ligge i SAMME scope som barnet — en seksjon under et annet års avdeling ville
     * tegnet en gruppeoverskrift som hører til et annet år. Håndhevet i tjenesten, som dybden.
     */
    parentDepartmentId: z.uuid().nullish(),
    /**
     * LEDERNE OG MEDLEMMENE ALLEREDE VED OPPRETTING (QA13 #82, H1). «Ny avdeling» hadde bare navn
     * og plassering; lederne kunne settes først når avdelingen fantes. Samme regler som i
     * `updateDepartmentRequestSchema`; medlemmene bare i en årgangs tre.
     */
    leadPersonIds: z.array(z.uuid()).max(20).optional(),
    memberPersonIds: z.array(z.uuid()).max(500).optional(),
  })
  .meta({ id: 'CreateDepartmentRequest' });

/**
 * Renaming, recolouring, reordering or reparenting a department.
 *
 * NO `key`. The slug is generated once from the first name and never moves again, and that is
 * load-bearing in three places:
 *
 *   * the seed writes departments by key, and the Phase 2 edition clone copies the org-level tree
 *     into a year by key;
 *   * `create` restores a soft-deleted department when a new one is created with the same key,
 *     keeping its id so every task ever filed under it still points somewhere;
 *   * a task's group is the department ROW, so the key is not what a reader sees — the name is.
 *
 * Regenerating the key on rename would quietly break the first two to change nothing a person
 * looks at. So "Produksjon" renamed to "Produksjon og rigg" keeps `produksjon`, and the name is
 * the only thing that moves.
 *
 * Every field is optional and at least one must be present; a PATCH that changes nothing is a
 * request nobody meant to send.
 */
export const updateDepartmentRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(80).optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    /**
     * `null` promotes a section back to a top-level department; a uuid makes it a section of
     * that one. The two-level cap and the "not your own descendant" rule are enforced by the
     * service, as they are on create (§6.12).
     */
    parentDepartmentId: z.uuid().nullish(),
    /**
     * LEDERNE, HELE LISTA PÅ EN GANG (K-4).
     *
     * Samme form som «Gjelder scener» på et scenetidspunkt, og av samme grunn: å bytte leder er
     * ETT valg, og en rekkefølge av legg-til/fjern-kall har en mellomtilstand der avdelingen står
     * uten leder. Tom liste er «ingen leder» og er en gyldig tilstand — de fleste avdelinger har
     * ingen før noen setter en.
     */
    leadPersonIds: z.array(z.uuid()).max(20).optional(),
    /**
     * MEDLEMMENE SOM IKKE LEDER, HELE LISTA (239.33/F33). En leder er alltid med, så en leder som
     * mangler her, blir stående; lista styrer bare de andre.
     */
    memberPersonIds: z.array(z.uuid()).max(500).optional(),
  })
  .refine((body) => Object.values(body).some((value) => value !== undefined), {
    message: 'At least one field must be present.',
  })
  .meta({ id: 'UpdateDepartmentRequest' });

// ---------------------------------------------------------------------------- tasks
/**
 * The assignee, as the list needs to render them: the person, and the contact row this
 * organization knows them through.
 *
 * `contactId` and `displayName` are nullable together, and only in one state: a contact that was
 * soft-deleted after the task was assigned. The task keeps pointing at the person — that is what
 * `person_id` is for — and the client renders an unknown-person placeholder rather than losing
 * the row.
 */
export const taskAssigneeDtoSchema = z
  .object({
    personId: z.uuid(),
    contactId: z.uuid().nullable(),
    displayName: z.string().nullable(),
  })
  .meta({ id: 'TaskAssignee' });

/**
 * ET ANNET HUS, ved navn: eieren av en delt oppgave (`sharedFrom`), eller huset en kommentar eller
 * en loggrad kom fra (sak 209). Null overalt der det er leserens eget hus.
 */
export const taskHouseDtoSchema = z
  .object({ organizationId: z.uuid(), name: z.string() })
  .meta({ id: 'TaskSharedFrom' });

/** The group header for `?sort=edition`, and the festival badge on every other sort. */
export const taskEditionDtoSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    year: z.number().int(),
    festivalId: z.uuid(),
    festivalName: z.string(),
  })
  .meta({ id: 'TaskEdition' });

/** The group header for `?sort=department`. Data, not an enum (principle 9). */
export const taskDepartmentDtoSchema = z
  .object({
    id: z.uuid(),
    key: z.string(),
    name: z.string(),
    /* No colour. The chip on a task draws `categoricalColorVar(id)` like every other surface —
       see the note on `departmentDtoSchema`. */
  })
  .meta({ id: 'TaskDepartment' });

/**
 * Hvilken artist oppgaven hører til.
 *
 * ---------------------------------------------------------------------------------------------
 * ATLE, 1. SEPTEMBER: «Her må vi finne en god systematisk måte på at en task knyttes til en
 * artist. Også når man ser den på tasks i organisasjonsvinduet.»
 *
 * The link existed — `tasks.performance_id` has been a column since the booking page was built,
 * and the act's own tab filters on it — but it was invisible from the other side. In the
 * organisation's task list a row said which årgang and which department it belonged to and
 * nothing about WHICH ACT, so «alt som står igjen på Nordlys Allstars» was a question you could
 * only ask by opening the act. That is the half that was missing, and it is a display fact rather
 * than a new model.
 *
 * `title` is THE ACT'S OWN NAME, resolved the same way every other surface resolves it: the
 * booking's title if it has one, else the artist contact's display name. Three screens compute
 * that fallback and they must not disagree about what an act is called.
 */
export const taskPerformanceDtoSchema = z
  .object({
    id: z.uuid(),
    title: z.string(),
    editionId: z.uuid(),
  })
  .meta({ id: 'TaskPerformance' });
export type TaskPerformanceDto = z.infer<typeof taskPerformanceDtoSchema>;

/**
 * SPILLESTEDETS ARRANGEMENT oppgaven hører til (sak 229, 0449). Null for alle andre oppgaver.
 *
 * De tre navnefeltene og ikke ett ferdig navn: hva et arrangement heter (tittelen, ellers
 * artisten, ellers «Ikke avklart for arrangøren») er en oversatt setning, og API-et oversetter
 * aldri. Nettsiden setter det sammen med `nightLabel`, som lista over arrangementer gjør.
 */
export const taskVenueNightDtoSchema = z
  .object({
    id: z.uuid(),
    nightOn: z.iso.date(),
    title: z.string().nullable(),
    artistName: z.string().nullable(),
    promoterName: z.string().nullable(),
  })
  .meta({ id: 'TaskVenueNight' });
export type TaskVenueNightDto = z.infer<typeof taskVenueNightDtoSchema>;

/* ================================================================================================
 * KOI Del 1 — struktur, kobling, arbeidsflyt og historikk (KOI-ORDRE §3 Del 1).
 *
 * Alt under denne linja er det som gjorde oppgaven til en oppgave: delmål, etiketter, «knyttet
 * til», følgere, «Min dag», aktivitetsloggen, masseredigering og hurtiglegg. Migrasjon 0220 er
 * databasehalvdelen; dette er kontrakten.
 * ============================================================================================= */

/**
 * ETIKETTFARGENE ER ARMBÅNDENES TI (avgjørelse 4).
 *
 * Ikke en kopi av lista, men den samme lista: fargene løses opp i det samme tokenparet i
 * `globals.css`, og to lister som skulle holdt seg like er to lister som før eller siden ikke er
 * det. At konstanten heter noe med akkreditering er prisen for å slippe den kopien — og en
 * `LABEL_COLOURS` som var en egen `as const` ville kostet en skranke til i databasen.
 */
export const TASK_LABEL_COLOURS = ACCREDITATION_COLOURS;
export const taskLabelColourSchema = z.enum(TASK_LABEL_COLOURS);
export type TaskLabelColour = z.infer<typeof taskLabelColourSchema>;

/** En etikett, slik den tegnes som chip på et kort, en rad og i panelet. */
export const taskLabelDtoSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    colour: taskLabelColourSchema,
    sortOrder: z.number().int(),
  })
  .meta({ id: 'TaskLabel' });

/**
 * DELMÅL (avgjørelse 3). `doneBy` er med fordi «hvem krysset av» er det eneste spørsmålet noen
 * stiller om et delmål etterpå — og det er én join på en liste som allerede er lastet.
 */
export const taskChecklistItemDtoSchema = z
  .object({
    id: z.uuid(),
    title: z.string(),
    done: z.boolean(),
    sortOrder: z.number().int(),
    doneAt: z.iso.datetime().nullable(),
    doneBy: taskAssigneeDtoSchema.nullable(),
  })
  .meta({ id: 'TaskChecklistItem' });

/**
 * «3 av 7», som lista og kortet trenger den.
 *
 * TO TALL OG IKKE HELE LISTA PÅ SAMMENDRAGET, fordi en liste på førti oppgaver da ville båret
 * førti sjekklister ingen ser. Panelet henter punktene; lista teller dem.
 */
export const taskChecklistProgressSchema = z
  .object({
    done: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
  })
  .meta({ id: 'TaskChecklistProgress' });

/** Hva en oppgave kan være knyttet til (avgjørelse 7). Rekkefølgen er den panelet grupperer i. */
export const TASK_LINK_OBJECT_TYPES = [
  /* Sak 216.9 (0395): bookingen byrået gir artisten en oppgave på. */
  'agency_booking',
  'booking_enquiry',
  'person',
  'resource',
  'performance',
  'shift',
  'stage',
  'document',
  'thread',
] as const;
export type TaskLinkObjectType = (typeof TASK_LINK_OBJECT_TYPES)[number];
export const taskLinkObjectTypeSchema = z.enum(TASK_LINK_OBJECT_TYPES);

/**
 * «KNYTTET TIL» — én chip.
 *
 * `label` ER LØST OPP I SPØRRINGEN og kan likevel være null: lenka har ingen fremmednøkkel (se
 * `task_links` i skjemaet), så en slettet ressurs etterlater en rad som ikke løser opp. Klienten
 * tegner en grå chip med typen og ingen tittel framfor å skjule den — en lenke som forsvinner uten
 * at noen sa fra er verre enn en som står og ser død ut.
 *
 * `secondary` er linja under: avdelingen på en person, scenen på en programpost, typen på en
 * ressurs. Den er alltid valgfri og aldri noe klienten regner med.
 */
export const taskLinkDtoSchema = z
  .object({
    id: z.uuid(),
    objectType: taskLinkObjectTypeSchema,
    objectId: z.uuid(),
    label: z.string().nullable(),
    secondary: z.string().nullable(),
  })
  .meta({ id: 'TaskLink' });

/**
 * ÉN RAD I AKTIVITETSLOGGEN.
 *
 * `field` er maskinlesbar og `oldValue`/`newValue` er FORMATERTE VERDIER, aldri setninger:
 * API-et returnerer aldri en oversatt setning (prinsipp 8), så «Siri flyttet fristen 13. → 20.
 * sep.» settes sammen i klienten av `t('tasks.activity.' + field, { from, to, actor })`.
 *
 * Feltnavnene er kolonnenavn (`status`, `due_at`, `assignee_person_id`, `priority`, `title`,
 * `description`, `department_id`, `starts_at`) pluss hendelser som ikke er kolonner (`created`,
 * `comment`, `checklist`, `label`, `link`, `allocation`, `recurrence`).
 */
export const taskActivityDtoSchema = z
  .object({
    id: z.uuid(),
    field: z.string(),
    oldValue: z.string().nullable(),
    newValue: z.string().nullable(),
    /** Null = systemet. En gjentakelse som lager neste forekomst har ingen person bak seg. */
    actor: taskAssigneeDtoSchema.nullable(),
    /** Huset aktøren handlet fra, når det ikke er leserens eget (sak 209). */
    actorOrganization: taskHouseDtoSchema.nullable(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: 'TaskActivity' });

export const taskSummaryDtoSchema = z
  .object({
    id: z.uuid(),
    title: z.string(),
    /**
     * Markdown. På sammendraget og ikke bare på detaljformen siden bølge 3 (29.09): lista har en
     * «Beskrivelse»-kolonne under «Kolonner», og feltet leses i samme spørring som raden. Den
     * samme regelen for hvem som ser den som panelet — raden og beskrivelsen er én oppgave.
     */
    description: z.string().nullable(),
    status: taskStatusSchema,
    priority: taskPrioritySchema,
    /** NULL sorts last in every list this API offers, in both directions. */
    dueAt: z.iso.datetime().nullable(),
    /** Resolved in the same query as the row (principle 6: no N+1). Never a second round trip. */
    assignee: taskAssigneeDtoSchema.nullable(),
    /**
     * Who raised it, resolved the same way and in the same query.
     *
     * ON THE SUMMARY AND NOT ONLY ON THE DETAIL SHAPE, because `task:update` and `task:delete`
     * resolve through `isCreatorOrAssignee` for `limited` and `member`. Without it a list row
     * could answer only half that question, and a member who raised a task and assigned it to
     * somebody else was shown no delete action although the API would have allowed it.
     *
     * Null when nobody created it — `created_by` is nullable, for a row a migration or the
     * system wrote — and `displayName` is null when the creator is a person whose contact card
     * has since been soft-deleted, exactly as on `assignee`.
     */
    creator: taskAssigneeDtoSchema.nullable(),
    /** NULL = a personal or organization-level task, which is a first-class state (§6.16). */
    edition: taskEditionDtoSchema.nullable(),
    department: taskDepartmentDtoSchema.nullable(),
    /** Which act, when the task belongs to one — see `taskPerformanceDtoSchema`. */
    performance: taskPerformanceDtoSchema.nullable(),
    /** Spillestedets arrangement, når oppgaven hører til ett (0449). */
    venueNight: taskVenueNightDtoSchema.nullable(),
    /**
     * DEN ANDRE PARTENS OPPGAVE PÅ EN DELT ADVANCE (QA 23. september, punkt 2 — 0365).
     *
     * Null for en oppgave i leserens eget hus, som er nesten alle. Satt når oppgaven er festivalens
     * og leseren byrået, eller omvendt: da kan den leses, kommenteres og fullføres eller åpnes
     * igjen, men ikke endres eller slettes (sak 209), og huset er det som sier hvem som eier den.
     */
    sharedFrom: taskHouseDtoSchema.nullable(),
    completedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),

    /* ------------------------------------------------------------------ KOI Del 1
     * SEKS FELT PÅ SAMMENDRAGET, og hvert av dem tegnes i en kolonne lista faktisk har.
     *
     * Grensa mellom det som er her og det som bare er på detaljformen går ved ÉN TING: kan det
     * regnes ut i spørringen som allerede fant siden, uten en runde til per rad? Etikettene,
     * sjekklistetellingen, kommentartellingen og vedleggstellingen er aggregater over siden —
     * femti rader, fire delspørringer, én rundtur. Sjekklistas PUNKTER, lenkene og aktiviteten er
     * det ikke, og de blir stående på detaljformen.
     */
    /** Tidslinjas venstre ende. Null er det vanlige: de fleste oppgaver har bare en frist. */
    startsAt: z.iso.datetime().nullable(),
    /** RFC 5545. Bare til å tegne gjentakelsesikonet — regelen leses i panelet. */
    rrule: z.string().nullable(),
    estimateMinutes: z.number().int().nullable(),
    labels: z.array(taskLabelDtoSchema),
    /** «3/7». Begge null-tallene er en oppgave uten sjekkliste, og da tegnes ingenting. */
    checklist: taskChecklistProgressSchema,
    commentCount: z.number().int().nonnegative(),
    attachmentCount: z.number().int().nonnegative(),
    /**
     * STÅR OPPGAVEN ET STED PÅ KARTET (QA13 runde 2, #36c)?
     *
     * Atle: «Stedet vises på oppgaven, som kartnål i lista og som markør i kartvisningen.» De to
     * andre fantes: panelet har `ObjectPlaceSection`, og kartvisningen tegner markøren. Lista
     * hadde ingenting.
     *
     * ET FLAGG OG IKKE PUNKTET. Lista trenger å vite AT det finnes et sted, ikke hvor — nåla er
     * en invitasjon til å åpne oppgaven, og koordinatene ville vært to tall ingen leser. Og det
     * er forskjellen som gjør feltet gratis: en `exists` på en spørring som allerede kjører,
     * framfor et andre kall som henter hvert punkt i årgangen for å tegne ett ikon per rad.
     *
     * `useEditionMap` er med vilje SLÅTT AV utenfor kartvisningen — «to kall på hver eneste
     * visning av oppgavelista er to kall for noe ingen ser» — så lista kunne ikke fått svaret
     * derfra uten å oppheve den regelen.
     */
    hasPlace: z.boolean(),
    /**
     * Står den på LESERENS dag i dag? Per kaller, ikke per oppgave — det er hele poenget med
     * `task_my_day` (se skjemaet), og det er derfor feltet ikke kan være en kolonne på `tasks`.
     */
    myDay: z.boolean(),
  })
  .meta({ id: 'TaskSummary' });

/**
 * What one task is waiting for, or what is waiting for it (PAGES.md: «én liste med 'venter på'»).
 *
 * A SUMMARY OF A TASK RATHER THAN A TaskSummaryDto, and much thinner than one: a dependency row is
 * a line saying "this is not done yet". It needs the title, whether it is finished, and somewhere to
 * click — not the assignee, the edition, the department and the deadline, which would make the
 * detail page's query resolve five joins per blocker.
 *
 * `status` rather than a boolean, because "blocked by something cancelled" and "blocked by something
 * in progress" are different sentences and only the row knows which.
 */
export const taskDependencyDtoSchema = z
  .object({
    id: z.uuid(),
    title: z.string(),
    status: taskStatusSchema,
    dueAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'TaskDependency' });

/** One comment on a task. Flat and chronological — not a thread; see the schema's own note. */
export const taskCommentDtoSchema = z
  .object({
    id: z.uuid(),
    taskId: z.uuid(),
    /** Null for a system comment, and for one whose author's contact card was removed. */
    author: taskAssigneeDtoSchema.nullable(),
    /** Huset forfatteren skrev fra, når det ikke er leserens eget (sak 209). */
    authorOrganization: taskHouseDtoSchema.nullable(),
    body: z.string(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'TaskComment' });

export const taskDtoSchema = taskSummaryDtoSchema
  .extend({
    /**
     * A bare id, unlike `creator` above, and deliberately so: it is only ever compared, never
     * shown. Rendering "Fullført av …" needs a name, and that would be a sixth join on a page
     * that has no such copy yet. When it does, this becomes a `taskAssigneeDtoSchema` too.
     */
    completedByPersonId: z.uuid().nullable(),
    /**
     * MEDANSVARLIGE — the other people on it. Never includes `assignee`: that is the ONE person
     * answerable, and a list that repeated them would make the two fields say the same thing.
     *
     * On the DETAIL shape and not on the summary, and that is a performance decision rather than a
     * product one: a list of forty tasks would need forty aggregates to carry it, and no list view
     * in `PAGES.md` shows co-assignees. The board and the list group by `assignee`.
     */
    coAssignees: z.array(taskAssigneeDtoSchema),
    /**
     * «Venter på» — what must finish before this can. Inline rather than a second request for the
     * same reason a message's files are: the detail page needs them to draw at all, and they come
     * back in the query that already found the task.
     */
    blockedBy: z.array(taskDependencyDtoSchema),
    /** The other direction, off the same rows: what is waiting for THIS task. */
    blocking: z.array(taskDependencyDtoSchema),

    /* ------------------------------------------------------------------ KOI Del 1 */
    /** Påminnelsen. Egen kolonne og ikke «X timer før frist» — se migrasjon 0220. */
    remindAt: z.iso.datetime().nullable(),
    /** Punktene, i den rekkefølgen noen la dem. Panelet tegner dem; lista teller dem. */
    checklistItems: z.array(taskChecklistItemDtoSchema),
    /** «Knyttet til» — chipsene. Tomt er den vanlige tilstanden. */
    links: z.array(taskLinkDtoSchema),
    /** Følgerne. Aldri ansvarlig eller medansvarlige — de er allerede over. */
    watchers: z.array(taskAssigneeDtoSchema),
  })
  .meta({ id: 'Task' });

export const listTasksQuerySchema = cursorQuerySchema(TASK_SORT_KEYS)
  .extend({
    /**
     * The global view is this filter set to the caller's own person id. It is a filter rather
     * than a `mine` flag because the same endpoint answers "hva ligger på Kari" for an admin,
     * and a boolean that means a different thing per caller is a boolean that gets audited
     * wrong.
     */
    assigneePersonId: z.uuid().optional(),
    editionId: z.uuid().optional(),
    /**
     * HVILKE ARRANGEMENTER MAN VIL SE (QA13 runde 2, #39).
     *
     * Atle: «Det skal finnes et valg for hvilke arrangementer man ser i org-kalenderen OG I
     * OPPGAVER: avkrysningsliste med "Organisasjonen", hver festival/venue og årgang, pluss
     * hurtigvalgene "Alle" og "Bare organisasjonen".»
     *
     * Samme form og samme begrunnelse som `calendarItemsQuerySchema.editionIds`, med én forskjell
     * som er verdt å skrive ned: HER FINNES BEGGE PARAMETRENE, og de kan ikke brukes sammen.
     *
     * `editionId` er årgangens EGEN liste — `/festivals/:f/editions/:e/tasks`, der året er stien
     * og leseren ikke kan endre det. `editionIds` er utvalget leseren har krysset av på
     * organisasjonens liste. To parametre som kan si hver sin ting om det samme er nøyaktig det
     * `search-params.ts` avviser for gruppering og sortering — så et kall som sender begge er en
     * 400 og ikke en stille prioritering av den ene.
     *
     * UTELATT BETYR ALLE. En adresse som bærer hele lista ser filtrert ut.
     */
    editionIds: editionIdsQuerySchema.optional(),
    /**
     * «Samme oppgavevisning som allerede er bygget, filtrert på denne performancen»
     * (PERFORMANCE.md, fane 3). A FILTER, not a second list: the tab is this endpoint with one
     * more parameter, so a task written there is an ordinary task and shows up in the
     * festival's own Tasks page too — which the spec asks for in the next sentence.
     */
    performanceId: z.uuid().optional(),
    /**
     * SPILLESTEDETS ARRANGEMENT (sak 229, 0449): fanen Oppgaver på ett arrangement. Et filter på
     * lista alle har, som `performanceId` — en oppgave skrevet der er en vanlig oppgave i huset.
     * I motsetning til `performanceId` løfter den ikke husgrensen: et arrangement er aldri delt.
     */
    venueNightId: z.uuid().optional(),
    /**
     * HELE HUSET, ALLE ÅRGANGENE (sak 229). Spillestedet har årganger brukeren aldri ser
     * (`ensure_venue_edition`), så husets oversikt kan ikke spørre med en `editionId`. Dette er
     * «alle årganger i dette spillestedet/festivalen», løst i basen.
     */
    festivalId: z.uuid().optional(),
    departmentId: z.uuid().optional(),
    /**
     * FRITEKSTSØK (migrering 0107). Atle, 9. september: «ALLE TABELLER MÅ KUNNE SØKES OG
     * SORTERES!!!»
     *
     * I BASEN OG IKKE I KLIENTEN, fordi lista er paget: et filter i nettleseren ville søkt i den
     * siden som tilfeldigvis var hentet, og svart «ingen treff» når det står tolv på side tre. Det
     * er en feil ingen kan se. Går gjennom `app.search_task_ids`, som kontaktsøket og
     * artistsøket gjør, mot en trigram-indeks.
     *
     * Søker i tittelen og beskrivelsen. IKKE kommentarene: de er en samtale, og en oppgave
     * som dukker opp fordi noen nevnte et ord for tre uker siden er et treff den som søkte ikke
     * kan forklare.
     */
    search: z.string().trim().min(1).max(120).optional(),
    /** OR within the filter: a task matches if its status is **any** of the ones asked for. */
    status: statusListQuerySchema.optional(),
    /** The same, for priority. `PAGES.md` lists both among the filters Tasks [F1] offers. */
    priority: priorityListQuerySchema.optional(),
    /**
     * «FORFALT» — past its deadline and still open.
     *
     * Two conditions, and the second is what makes it mean anything: a task finished last week was
     * late, but it is not something to do today. `done` and `cancelled` are both excluded, because
     * a cancelled task is not work either.
     *
     * It costs no index. The default sort is the deadline, so `due_at < now()` is a range on the
     * leading column of `ix_tasks__org_due_key` — the same index the page already reads in order —
     * and the status test is a cheap filter on the handful of rows that survive it. A task with no
     * deadline is never overdue: `NULL < now()` is unknown, which is the answer we want.
     */
    overdue: overdueQuerySchema.optional(),
    /**
     * THE DEADLINE WINDOW, AND IT IS WHAT MAKES THE CALENDAR'S DAY VIEW AN ORDINARY LIST.
     *
     * Both bounds are CALENDAR DAYS and both are INCLUSIVE, so one day is `dueFrom === dueTo`.
     * `tasks.due_at` is a `timestamptz`, so the server resolves a day to an interval in the
     * organisation's zone rather than the reader's — a deadline at 23:00 in Oslo belongs to that
     * Oslo day for everyone looking at it, including a colleague in New York.
     *
     * Added with the calendar rather than for it. The month grid asks `/v1/calendar` for counts,
     * because fifty tasks a day cannot be listed in a cell; clicking a day then asks THIS list for
     * that one day. The day detail is therefore not a special endpoint with its own sorting, its
     * own permissions and its own drift — it is the list everybody already has.
     *
     * A task with no deadline matches neither bound. `due_at` is nullable and those tasks are real;
     * they simply are not on a calendar.
     */
    dueFrom: z.iso.date().optional(),
    dueTo: z.iso.date().optional(),

    /* ------------------------------------------------------------------ KOI Del 1
     * ETIKETTFILTERET, «MIN DAG» OG «JEG FØLGER» — tre chips i toolbaren, tre parametre her.
     *
     * Hver av dem er en `EXISTS` mot en lenketabell med en indeks som leder på nøyaktig det
     * predikatet (`ix_task_label_links__org_label_task`, `ix_task_my_day__org_person_day`,
     * `ix_task_watchers__org_person_task`), og ingen av dem rører sorteringen. Det er derfor de er
     * filtre og ikke egne endepunkter: «Min dag» er lista med ett ledd til.
     */
    labelId: z.uuid().optional(),
    /**
     * «MIN DAG», og den er en DATO framfor en boolsk verdi med vilje.
     *
     * `task_my_day` bærer dagen, så «hva sto på dagen min i går» er det samme kallet med en annen
     * verdi — og en `myDay=true` ville måttet finne på hvilken dag den mente, i hvilken sone.
     * Klienten vet hvilken dag den tegner; den sier det.
     */
    myDay: z.iso.date().optional(),
    /** «Jeg følger» — oppgaver den innloggede er watcher på. Alltid kalleren selv. */
    watching: overdueQuerySchema.optional(),
    /**
     * «Oppgavene på denne bookingen» (sak 216.9): oppgaver med en `task_links`-rad av typen og id-en.
     * Begge eller ingen. Huset er alltid vårt — en booking er ikke delt slik en programpost er.
     */
    linkType: taskLinkObjectTypeSchema.optional(),
    linkId: z.uuid().optional(),
  })
  .refine((q) => (q.linkType === undefined) === (q.linkId === undefined), {
    message: 'Send linkType and linkId together.',
    path: ['linkId'],
  })
  .refine((q) => q.dueFrom === undefined || q.dueTo === undefined || q.dueTo >= q.dueFrom, {
    message: 'The deadline window must end on or after it starts.',
    path: ['dueTo'],
  })
  /* Se `editionIds`: årgangens egen liste og leserens utvalg er to spørsmål, aldri ett. */
  .refine((q) => q.editionId === undefined || q.editionIds === undefined, {
    message: 'Send either editionId or editionIds, not both.',
    path: ['editionIds'],
  })
  .meta({ id: 'ListTasksQuery' });

export const taskListResponseSchema = page(taskSummaryDtoSchema, 'TaskListResponse');

export const createTaskRequestSchema = z
  .object({
    title: titleSchema,
    description: descriptionSchema.nullish(),
    /** Chosen from Contacts: the person must be a live contact of this organization. */
    assigneePersonId: z.uuid().nullish(),
    dueAt: z.iso.datetime().nullish(),
    /** Omitted: `normal`, the column default. */
    priority: taskPrioritySchema.optional(),
    /** Omitted or null: a personal / organization-level task. */
    editionId: z.uuid().nullish(),
    /** The booking this work is about. Most tasks have none — see the column’s own comment. */
    performanceId: z.uuid().nullish(),
    /**
     * Spillestedets arrangement (sak 229, 0449). SERVEREN SETTER ÅRGANGEN fra arrangementet: huset
     * ser aldri årgangen, så klienten kan ikke vite den. En `editionId` ved siden av overstyres.
     */
    venueNightId: z.uuid().nullish(),
    departmentId: z.uuid().nullish(),

    /* ------------------------------------------------------------------ KOI Del 1 */
    startsAt: z.iso.datetime().nullish(),
    rrule: rruleSchema.nullish(),
    remindAt: z.iso.datetime().nullish(),
    estimateMinutes: z.number().int().min(1).max(100_000).nullish(),
    /**
     * Etikettene, satt ved opprettelse så hurtiglegget kan gjøre alt i ett kall. Hele lista,
     * aldri «legg til én» — samme form som medansvarlige, av samme grunn.
     */
    labelIds: z.array(z.uuid()).max(MAX_TASK_LABELS_PER_TASK).optional(),
  })
  .meta({ id: 'CreateTaskRequest' });

export const updateTaskRequestSchema = z
  .object({
    title: titleSchema.optional(),
    description: descriptionSchema.nullish(),
    /**
     * Re-assignment. Asked of `can()` a second time as `task:assign`, because handing work to
     * somebody else is a different permission from editing its title — see the controller.
     */
    assigneePersonId: z.uuid().nullish(),
    dueAt: z.iso.datetime().nullish(),
    priority: taskPrioritySchema.optional(),
    editionId: z.uuid().nullish(),
    performanceId: z.uuid().nullish(),
    departmentId: z.uuid().nullish(),

    /* ------------------------------------------------------------------ KOI Del 1 */
    startsAt: z.iso.datetime().nullish(),
    rrule: rruleSchema.nullish(),
    remindAt: z.iso.datetime().nullish(),
    estimateMinutes: z.number().int().min(1).max(100_000).nullish(),
    labelIds: z.array(z.uuid()).max(MAX_TASK_LABELS_PER_TASK).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateTaskRequest' });

/**
 * The status transition. Its own endpoint, deliberately: see decision 1 in the file header.
 *
 * `completed_at` is not a field of it. `ck_tasks__completed_consistency` requires
 * `(status = 'done') = (completed_at IS NOT NULL)`, so the pair is maintained by the service and
 * a client cannot put the row in a state the database will reject.
 */
export const updateTaskStatusRequestSchema = z
  .object({ status: taskStatusSchema })
  .meta({ id: 'UpdateTaskStatusRequest' });

/**
 * The most people one request may put on a task, and the most it may make it wait for.
 *
 * Bounded because both become one bind per id in the SQL that writes them, and because a task with
 * fifty co-assignees is a task nobody owns. Generous enough that no real crew hits either.
 */
export const MAX_TASK_CO_ASSIGNEES = 20;
export const MAX_TASK_DEPENDENCIES = 20;

/** How long a comment may be. The same ceiling a message has: long enough, not a document. */
export const MAX_TASK_COMMENT = 20_000;

/**
 * REPLACES the whole list, rather than adding one and removing one.
 *
 * Two people editing the same task's co-assignees is a real thing on a production week, and PATCH
 * semantics on a set — «add Kari», «remove Ola» — makes the outcome depend on the order the two
 * requests arrive in. A whole list is idempotent: whoever writes last wrote what they saw, and
 * `audit_log` records both attempts with both lists.
 *
 * The same argument as the participant list on a thread, reached independently.
 */
export const setTaskCoAssigneesRequestSchema = z
  .object({
    personIds: z.array(z.uuid()).max(MAX_TASK_CO_ASSIGNEES),
  })
  .meta({ id: 'SetTaskCoAssigneesRequest' });

/** «Venter på», replaced whole for the same reason. A task's own id is refused by the service. */
export const setTaskDependenciesRequestSchema = z
  .object({
    blockedByTaskIds: z.array(z.uuid()).max(MAX_TASK_DEPENDENCIES),
  })
  .meta({ id: 'SetTaskDependenciesRequest' });

export const createTaskCommentRequestSchema = z
  .object({
    body: z.string().trim().min(1).max(MAX_TASK_COMMENT),
  })
  .meta({ id: 'CreateTaskCommentRequest' });

/**
 * NOT PAGED, and the same argument attachments make: the collection is the comments on ONE task.
 * A task whose discussion runs past a page is a task that should have been a thread, and a cursor
 * here would be machinery for a case that does not exist while costing every caller a paging loop.
 */
export const taskCommentListResponseSchema = z
  .object({ items: z.array(taskCommentDtoSchema), ...pageCursorFields })
  .meta({ id: 'TaskCommentListResponse' });

export type DepartmentDto = z.infer<typeof departmentDtoSchema>;
export type ListDepartmentsQuery = z.infer<typeof listDepartmentsQuerySchema>;
export type DepartmentListResponse = z.infer<typeof departmentListResponseSchema>;
export type CreateDepartmentRequest = z.infer<typeof createDepartmentRequestSchema>;
export type UpdateDepartmentRequest = z.infer<typeof updateDepartmentRequestSchema>;

export type TaskAssigneeDto = z.infer<typeof taskAssigneeDtoSchema>;
export type TaskEditionDto = z.infer<typeof taskEditionDtoSchema>;
export type TaskDepartmentDto = z.infer<typeof taskDepartmentDtoSchema>;
export type TaskSummaryDto = z.infer<typeof taskSummaryDtoSchema>;
export type TaskDto = z.infer<typeof taskDtoSchema>;
export type ListTasksQuery = z.infer<typeof listTasksQuerySchema>;
export type TaskListResponse = z.infer<typeof taskListResponseSchema>;
export type CreateTaskRequest = z.infer<typeof createTaskRequestSchema>;
export type UpdateTaskRequest = z.infer<typeof updateTaskRequestSchema>;
export type UpdateTaskStatusRequest = z.infer<typeof updateTaskStatusRequestSchema>;

/* ================================================================================================
 * KOI Del 1 — endepunktene
 * ============================================================================================= */

/** Hvor mange delmål én oppgave får ha. Flere enn dette er en oppgave som burde vært flere. */
export const MAX_TASK_CHECKLIST_ITEMS = 100;
/** Hvor mange ting én oppgave får være knyttet til. */
export const MAX_TASK_LINKS = 50;
/** Hvor mange følgere. Samme tall som medansvarlige, av samme grunn. */
export const MAX_TASK_WATCHERS = 20;
/** Hvor mange oppgaver én masseredigering får røre. Én skjermfull ganger fire. */
export const MAX_TASK_BULK = 200;

// ------------------------------------------------------------------------------------ sjekkliste

export const createTaskChecklistItemRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(200),
    /**
     * Utelatt: sist. Det er den eneste plasseringen «+ Legg til delmål» kan mene, og en klient som
     * må regne ut neste tall selv er en klient som regner feil når to personer skriver samtidig.
     */
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .meta({ id: 'CreateTaskChecklistItemRequest' });

export const updateTaskChecklistItemRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(200).optional(),
    /**
     * Avkryssingen. `done_at` og `done_by_person_id` settes og nulles av tjenesten ved siden av
     * den, fordi `ck_task_checklist_items__done_consistency` gjør de tre til ett faktum — samme
     * arbeidsdeling som `tasks.completed_at`.
     */
    done: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine((body) => Object.values(body).some((value) => value !== undefined), {
    message: 'At least one field must be present.',
  })
  .meta({ id: 'UpdateTaskChecklistItemRequest' });

/**
 * DRA FOR REKKEFØLGE — hele lista, ikke «flytt denne til plass 3».
 *
 * Samme argument som medansvarlige: to personer som drar i den samme lista skal ikke få et
 * resultat som avhenger av hvem som slapp først. Klienten sender rekkefølgen den ser, og
 * tjenesten skriver 0, 1, 2 … i den rekkefølgen.
 */
export const reorderTaskChecklistRequestSchema = z
  .object({ itemIds: z.array(z.uuid()).max(MAX_TASK_CHECKLIST_ITEMS) })
  .meta({ id: 'ReorderTaskChecklistRequest' });

export const taskChecklistListResponseSchema = z
  .object({ items: z.array(taskChecklistItemDtoSchema) })
  .meta({ id: 'TaskChecklistListResponse' });

// -------------------------------------------------------------------------------------- etiketter

/**
 * ETIKETTENE ER EN ORGANISASJONSLISTE, så de har sine egne ruter (`/v1/task-labels`) og ikke en
 * plass under en oppgave. Å knytte dem TIL en oppgave er `labelIds` på oppgaven — hele lista,
 * som medansvarlige.
 *
 * Ikke paget: en organisasjon har ti-tjue etiketter, og en meny man velger fra er ikke et
 * register man blar i. Samme kall som `task_templates`.
 */
export const taskLabelListResponseSchema = z
  .object({ items: z.array(taskLabelDtoSchema) })
  .meta({ id: 'TaskLabelListResponse' });

export const createTaskLabelRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(60),
    colour: taskLabelColourSchema.optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .meta({ id: 'CreateTaskLabelRequest' });

export const updateTaskLabelRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(60).optional(),
    colour: taskLabelColourSchema.optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine((body) => Object.values(body).some((value) => value !== undefined), {
    message: 'At least one field must be present.',
  })
  .meta({ id: 'UpdateTaskLabelRequest' });

// ------------------------------------------------------------------------------------ knyttet til

/**
 * «+ KNYTT» — hele lista, av samme grunn som alle de andre settene her.
 *
 * Paret `(objectType, objectId)` er nøkkelen, så den samme ressursen kan ikke knyttes to ganger;
 * tjenesten fjerner dubletter framfor å svare 400, som den gjør med medansvarlige.
 */
export const setTaskLinksRequestSchema = z
  .object({
    links: z
      .array(z.object({ objectType: taskLinkObjectTypeSchema, objectId: z.uuid() }))
      .max(MAX_TASK_LINKS),
  })
  .meta({ id: 'SetTaskLinksRequest' });

// ---------------------------------------------------------------------------------------- følgere

export const setTaskWatchersRequestSchema = z
  .object({ personIds: z.array(z.uuid()).max(MAX_TASK_WATCHERS) })
  .meta({ id: 'SetTaskWatchersRequest' });

// ---------------------------------------------------------------------------------------- min dag

/**
 * «MIN DAG» — på og av for ÉN oppgave, for den innloggede, på én dato.
 *
 * Ingen `personId` i kroppen, og det er en tilgangsregel og ikke en forkortelse: `task_my_day` er
 * mitt skrivebord. Å legge noe på en annens dag er å gi dem en oppgave, og det finnes allerede —
 * det heter `task:assign`.
 */
export const setTaskMyDayRequestSchema = z
  .object({
    /** Utelatt: i dag, i organisasjonens sone. Tjenesten regner den ut; klienten kan overstyre. */
    day: z.iso.date().optional(),
    onMyDay: z.boolean(),
  })
  .meta({ id: 'SetTaskMyDayRequest' });

// -------------------------------------------------------------------------------------- aktivitet

/**
 * IKKE PAGET, MEN AVKORTET. En oppgave med tusen endringer finnes ikke, men en oppgave som har
 * stått i to år og blitt flyttet ukentlig har hundre — og fanen skal tegne de siste, ikke alle.
 * Grensa er i tjenesten (`limit 200`), og svaret sier om det var flere.
 */
export const taskActivityListResponseSchema = z
  .object({
    items: z.array(taskActivityDtoSchema),
    ...pageCursorFields,
    /** Sant når loggen ble avkortet: klienten skriver «Viser de 200 siste». */
    truncated: z.boolean(),
  })
  .meta({ id: 'TaskActivityListResponse' });

// -------------------------------------------------------------------------------- masseredigering

/**
 * `PATCH /v1/tasks/bulk` — masse-linja nederst når flere rader er valgt.
 *
 * ---------------------------------------------------------------------------------------------
 * ÉN RUTE OG IKKE N KALL FRA KLIENTEN, og grunnen er hverken nettverk eller eleganse: det er
 * TILLATELSEN. Tjuefem enkeltkall er tjuefem transaksjoner som kan lykkes delvis, og en bruker som
 * fikk lov til tolv av dem sitter igjen med en liste der halvparten er flyttet. Her spørres `can()`
 * for HVER oppgave før noe skrives, og et eneste nei er et 403 for hele kallet.
 *
 * `status` STÅR HER SELV OM `updateTaskRequestSchema` IKKE HAR DET. Det er ikke en bakdør rundt
 * avgjørelse 1 i filhodet: tjenesten spør `task:complete` per oppgave når statusen er `done`, som
 * `POST /:id/status` gjør. Det ville vært en bakdør hvis den ikke gjorde det.
 *
 * Alle feltene er valgfrie og minst ett må være satt — en masseredigering som ikke endrer noe er
 * et kall ingen mente å sende.
 */
export const bulkUpdateTasksRequestSchema = z
  .object({
    taskIds: z.array(z.uuid()).min(1).max(MAX_TASK_BULK),
    status: taskStatusSchema.optional(),
    priority: taskPrioritySchema.optional(),
    assigneePersonId: z.uuid().nullish(),
    dueAt: z.iso.datetime().nullish(),
    departmentId: z.uuid().nullish(),
    /**
     * LEGG TIL OG FJERN, og ikke «sett lista».
     *
     * Dette er det ene settet i hele filen som ikke er helt-lista, og unntaket er selve poenget
     * med masseredigering: «merk disse femten med Haster» skal ikke ta bort etikettene de har fra
     * før. En hel liste ville gjort hver massehandling til en utjevning.
     */
    addLabelIds: z.array(z.uuid()).max(MAX_TASK_LABELS_PER_TASK).optional(),
    removeLabelIds: z.array(z.uuid()).max(MAX_TASK_LABELS_PER_TASK).optional(),
    /** Myk sletting, som `DELETE /v1/tasks/:id`. Kan ikke kombineres med de andre feltene. */
    deleted: z.literal(true).optional(),
  })
  .refine(
    (body) => Object.entries(body).some(([key, value]) => key !== 'taskIds' && value !== undefined),
    { message: 'At least one field must be given.' },
  )
  .refine(
    (body) =>
      body.deleted === undefined ||
      Object.entries(body).every(
        ([key, value]) => key === 'taskIds' || key === 'deleted' || value === undefined,
      ),
    { message: 'A bulk delete cannot also change fields.', path: ['deleted'] },
  )
  .meta({ id: 'BulkUpdateTasksRequest' });

/**
 * Hva massekallet gjorde. `changed` kan være mindre enn `taskIds.length` uten at noe gikk galt:
 * en oppgave som allerede sto på «Pågår» ble ikke skrevet, og det er riktig — `AuditedRepository`
 * skriver ikke en no-op og publiserer ingen hendelse for den.
 */
export const bulkUpdateTasksResponseSchema = z
  .object({
    requested: z.number().int().nonnegative(),
    changed: z.number().int().nonnegative(),
  })
  .meta({ id: 'BulkUpdateTasksResponse' });

// ------------------------------------------------------------------------------------ hurtiglegg

/**
 * `POST /v1/tasks/quick` — «Ring Hertz i morgen 14 @Thomas #Transport».
 *
 * ---------------------------------------------------------------------------------------------
 * TEKSTEN TOLKES PÅ SERVEREN, MED DEN SAMME FUNKSJONEN KLIENTEN BRUKER TIL Å VISE HVA DEN FORSTO
 * (`parseQuickTask` i `task-quick-text.ts`). Klienten kunne sendt en ferdig `CreateTaskRequest` og
 * spart en tolkning — og da ville tolkningen vært klientens, som er en tolkning en mobilapp, en
 * integrasjon og en e-post-inngang hver måtte gjort på nytt.
 *
 * `@navn` og `#avdeling` SLÅS OPP HER, og et navn som ikke finnes er ikke en feil: oppgaven lages
 * uten ansvarlig, og svaret sier hvilke deler som ikke traff. En hurtiglinje som svarer 400 fordi
 * du stavet «Thomass» er en hurtiglinje som er tregere enn skjemaet.
 */
export const createQuickTaskRequestSchema = z
  .object({
    text: z.string().trim().min(1).max(500),
    /** Årgangen oppgaven havner i. Utelatt: en personlig/organisasjonsoppgave, som ellers. */
    editionId: z.uuid().nullish(),
    performanceId: z.uuid().nullish(),
    /**
     * Dagens dato i LESERENS visning, `YYYY-MM-DD`. Utelatt: tjenesten bruker dagen i
     * organisasjonens sone.
     *
     * Den er med fordi «i morgen» er et spørsmål om hvilken dag det er der leseren sitter, og
     * serveren ikke kan vite det bedre enn klienten — men den kan svare når klienten ikke sier
     * noe. Se `task-quick-text.ts` for hvorfor selve tolkningen er sonefri.
     */
    today: z.iso.date().optional(),
    /**
     * Linja er skrevet under «Mine oppgaver» (217.27): uten et `@navn` blir oppgaven den som skrev.
     * Et `@navn` i teksten vinner. Å gi seg selv en oppgave krever ikke `task:assign`.
     */
    assignToMe: z.boolean().optional(),
  })
  .meta({ id: 'CreateQuickTaskRequest' });

/**
 * Oppgaven som ble laget, OG hva tolkningen gjorde med teksten.
 *
 * `unresolved` er det feltet som gjør hurtiglegget ærlig: skrev du `@Thomass` og det ikke finnes
 * noen som heter det, står oppgaven der uten ansvarlig og lista sier «fant ikke Thomass». Uten
 * det ville en tastefeil vært en stille utelatelse.
 */
export const createQuickTaskResponseSchema = z
  .object({
    task: taskDtoSchema,
    unresolved: z.array(z.object({ kind: z.enum(['assignee', 'department']), text: z.string() })),
  })
  .meta({ id: 'CreateQuickTaskResponse' });

// ---------------------------------------------------------------------------------------- tidslinje

/**
 * `GET /v1/tasks/timeline?from&to` — gantt-visningen.
 *
 * ---------------------------------------------------------------------------------------------
 * EGEN RUTE OG IKKE ET FILTER PÅ LISTA, og det er den ene gangen i denne ordren svaret ble en ny
 * rute. Grunnen er at spørsmålet er et annet: lista spør «de femti neste i denne rekkefølgen», og
 * tidslinja spør «ALT som overlapper dette vinduet» — uten markør, uten side, sortert på start.
 * Å presse det inn i `GET /v1/tasks` ville betydd en femte sortering, en `limit` som ikke kan
 * være 50, og et vindu som måtte sammenlignes mot to kolonner samtidig.
 *
 * VINDUET ER LUKKET I BEGGE ENDER OG MÅ VÆRE DET: en gantt uten ende er hele basen. Grensa er 400
 * dager, som er en festivalsesong pluss slakk.
 *
 * FILTRENE ER LISTAS, med samme navn og samme form (K6 M3). Tidslinja sendte bare fire av dem, og
 * med søket «Transport» og «Min dag» på viste den hele huset — under et søkefelt som sa noe annet.
 */
export const taskTimelineQuerySchema = z
  .object({
    from: z.iso.date(),
    to: z.iso.date(),
    editionId: z.uuid().optional(),
    editionIds: editionIdsQuerySchema.optional(),
    departmentId: z.uuid().optional(),
    assigneePersonId: z.uuid().optional(),
    status: statusListQuerySchema.optional(),
    priority: priorityListQuerySchema.optional(),
    overdue: overdueQuerySchema.optional(),
    search: z.string().trim().min(1).max(120).optional(),
    labelId: z.uuid().optional(),
    myDay: z.iso.date().optional(),
    watching: overdueQuerySchema.optional(),
  })
  .refine((q) => q.to >= q.from, {
    message: 'The window must end on or after it starts.',
    path: ['to'],
  })
  .refine((q) => q.editionId === undefined || q.editionIds === undefined, {
    message: 'Send either editionId or editionIds, not both.',
    path: ['editionIds'],
  })
  .meta({ id: 'TaskTimelineQuery' });

/**
 * En stolpe. `startsAt` er null for de aller fleste oppgaver, og da tegnes et PUNKT på fristen
 * framfor en stolpe fra ingensteds — som er hvorfor begge endene er nullbare og klienten ikke
 * regner med noen av dem.
 */
export const taskTimelineItemDtoSchema = z
  .object({
    id: z.uuid(),
    title: z.string(),
    status: taskStatusSchema,
    priority: taskPrioritySchema,
    startsAt: z.iso.datetime().nullable(),
    dueAt: z.iso.datetime().nullable(),
    assignee: taskAssigneeDtoSchema.nullable(),
    department: taskDepartmentDtoSchema.nullable(),
    labels: z.array(taskLabelDtoSchema),
    /** «Venter på», som pilene tegnes av. Bare id-ene: titlene står på radene selv. */
    blockedByTaskIds: z.array(z.uuid()),
  })
  .meta({ id: 'TaskTimelineItem' });

export const taskTimelineResponseSchema = z
  .object({
    items: z.array(taskTimelineItemDtoSchema),
    /** Sant når vinduet holdt flere enn taket. Klienten ber om et kortere vindu. */
    truncated: z.boolean(),
  })
  .meta({ id: 'TaskTimelineResponse' });

export type TaskLabelDto = z.infer<typeof taskLabelDtoSchema>;
export type TaskLabelListResponse = z.infer<typeof taskLabelListResponseSchema>;
export type CreateTaskLabelRequest = z.infer<typeof createTaskLabelRequestSchema>;
export type UpdateTaskLabelRequest = z.infer<typeof updateTaskLabelRequestSchema>;
export type TaskChecklistItemDto = z.infer<typeof taskChecklistItemDtoSchema>;
export type TaskChecklistProgress = z.infer<typeof taskChecklistProgressSchema>;
export type TaskChecklistListResponse = z.infer<typeof taskChecklistListResponseSchema>;
export type CreateTaskChecklistItemRequest = z.infer<typeof createTaskChecklistItemRequestSchema>;
export type UpdateTaskChecklistItemRequest = z.infer<typeof updateTaskChecklistItemRequestSchema>;
export type ReorderTaskChecklistRequest = z.infer<typeof reorderTaskChecklistRequestSchema>;
export type TaskLinkDto = z.infer<typeof taskLinkDtoSchema>;
export type SetTaskLinksRequest = z.infer<typeof setTaskLinksRequestSchema>;
export type SetTaskWatchersRequest = z.infer<typeof setTaskWatchersRequestSchema>;
export type SetTaskMyDayRequest = z.infer<typeof setTaskMyDayRequestSchema>;
export type TaskActivityDto = z.infer<typeof taskActivityDtoSchema>;
export type TaskActivityListResponse = z.infer<typeof taskActivityListResponseSchema>;
export type BulkUpdateTasksRequest = z.infer<typeof bulkUpdateTasksRequestSchema>;
export type BulkUpdateTasksResponse = z.infer<typeof bulkUpdateTasksResponseSchema>;
/**
 * PERSONENE SOM IKKE KAN FÅ EN OPPGAVE (217.8): aktene i lineupen. Velgerne trekker dem fra
 * kontaktlista; API-et avviser dem som ansvarlig uansett (`act_not_person`).
 */
export const taskUnassignableResponseSchema = z
  .object({ personIds: z.array(z.uuid()) })
  .meta({ id: 'TaskUnassignableResponse' });

export type TaskUnassignableResponse = z.infer<typeof taskUnassignableResponseSchema>;
export type CreateQuickTaskRequest = z.infer<typeof createQuickTaskRequestSchema>;
export type CreateQuickTaskResponse = z.infer<typeof createQuickTaskResponseSchema>;
export type TaskTimelineQuery = z.infer<typeof taskTimelineQuerySchema>;
export type TaskTimelineItemDto = z.infer<typeof taskTimelineItemDtoSchema>;
export type TaskTimelineResponse = z.infer<typeof taskTimelineResponseSchema>;

export type TaskDependencyDto = z.infer<typeof taskDependencyDtoSchema>;
export type TaskCommentDto = z.infer<typeof taskCommentDtoSchema>;
export type TaskCommentListResponse = z.infer<typeof taskCommentListResponseSchema>;
export type SetTaskCoAssigneesRequest = z.infer<typeof setTaskCoAssigneesRequestSchema>;
export type SetTaskDependenciesRequest = z.infer<typeof setTaskDependenciesRequestSchema>;
export type CreateTaskCommentRequest = z.infer<typeof createTaskCommentRequestSchema>;

/**
 * OPPGAVEMALER — «et sett standardoppgaver kan lagres og settes inn i et arrangement»
 * (PAGES.md, Tasks [F1]).
 *
 * ---------------------------------------------------------------------------------------------
 * THREE VERBS, AND THE PLAN NAMES TWO OF THEM: **lagres** and **settes inn**. The third — listing
 * what exists — falls out of having any at all.
 *
 * Editing a template's items one at a time is deliberately NOT here. `PAGES.md` puts «Oppgavemaler»
 * under Settings [F2], and what [F1] asks for is the mechanism plus a way to use it: capture the
 * work of a year that went well, and lay it down on the next one. A template that turns out wrong
 * is deleted and captured again, which is one round trip and no new contract.
 *
 * ---------------------------------------------------------------------------------------------
 * `dueOffsetDays` IS WHAT MAKES A TEMPLATE WORTH HAVING, and it is measured from the edition's own
 * `startsOn`. Negative is before the year starts, which is where production work lives: "book
 * backline" is −45, "print running order" is −2, "settle" is +7. A template of titles alone would
 * produce twenty undated tasks somebody then dates one at a time — most of the work it was meant to
 * save.
 *
 * Applying to an edition with no `startsOn` produces undated tasks rather than an error: the column
 * is nullable by design, and inventing a date would be worse than leaving the deadline off.
 */
export const MAX_TASK_TEMPLATE_ITEMS = 200;

/** How far either side of the year a standard deadline may sit. Five years is not a template. */
const OFFSET_DAYS = z.number().int().min(-1825).max(1825);

export const taskTemplateItemDtoSchema = z
  .object({
    id: z.uuid(),
    title: z.string(),
    description: z.string().nullable(),
    priority: taskPrioritySchema,
    /** The ORGANISATION's department, resolved. Null for work that belongs to no group. */
    department: taskDepartmentDtoSchema.nullable(),
    /** Days from the edition's `startsOn`. Negative is before it; null is no deadline. */
    dueOffsetDays: z.number().int().nullable(),
    sortOrder: z.number().int(),
  })
  .meta({ id: 'TaskTemplateItem' });

/**
 * A template and everything in it.
 *
 * ITEMS ARE INLINE AND THE LIST IS NOT PAGED, for the same reason a task's comments are not: the
 * collection is the items of ONE template, capped at `MAX_TASK_TEMPLATE_ITEMS`, and a cursor here
 * would be machinery for a case that does not exist while costing every caller a paging loop.
 */
export const taskTemplateDtoSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
    items: z.array(taskTemplateItemDtoSchema),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'TaskTemplate' });

/**
 * NOT PAGED. An organisation has a handful of templates — "Festivaloppsett", "Klubbkveld" — and a
 * list of them is a menu somebody picks from, not a register they page through.
 */
export const taskTemplateListResponseSchema = z
  .object({ items: z.array(taskTemplateDtoSchema) })
  .meta({ id: 'TaskTemplateListResponse' });

export const createTaskTemplateItemSchema = z
  .object({
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(5000).nullish(),
    priority: taskPrioritySchema.optional(),
    departmentId: z.uuid().nullish(),
    dueOffsetDays: OFFSET_DAYS.nullish(),
  })
  .meta({ id: 'CreateTaskTemplateItem' });

/**
 * Creating a template from scratch, items and all.
 *
 * WHOLE, NOT INCREMENTALLY. There is no "add an item" endpoint, because a template with one item
 * in it is not a state anybody wants to be in — it is a step on the way to a template, and a
 * half-written one in the list is something somebody else will apply by mistake.
 */
export const createTaskTemplateRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(5000).nullish(),
    items: z.array(createTaskTemplateItemSchema).max(MAX_TASK_TEMPLATE_ITEMS),
  })
  .meta({ id: 'CreateTaskTemplateRequest' });

/**
 * «LAGRES» — capture an edition's tasks as a template.
 *
 * THE OTHER WAY TO MAKE ONE, and the one the sentence in `PAGES.md` is actually about: a festival
 * that ran well has its year's work sitting in `tasks`, and the useful template is that, not
 * twenty rows typed again by hand.
 *
 * WHAT IS CAPTURED IS THE SHAPE, NOT THE STATE. Title, description, priority, group and the
 * DEADLINE AS AN OFFSET from that edition's `startsOn` — never the assignee, the status, the
 * completion or the id. A template that carried an assignee would hand next year's work to whoever
 * happened to do it last year, which is a decision the person applying it should make.
 *
 * Tasks with no deadline capture as `dueOffsetDays: null`, which is the same fact one level up.
 */
export const captureTaskTemplateRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(5000).nullish(),
    /** The year to read the work from. Its `startsOn` is what the offsets are measured against. */
    editionId: z.uuid(),
    /**
     * Whether to include work that is already finished.
     *
     * Off by default, and that is the useful default rather than the tidy one: a template is a
     * plan for next year, and the tasks that were cancelled or abandoned are exactly the ones
     * nobody wants back. `done` is the interesting exception — finished work is work that WAS
     * needed — so this flag lets it in while `cancelled` never comes.
     */
    includeCompleted: z.boolean().default(false),
  })
  .meta({ id: 'CaptureTaskTemplateRequest' });

/**
 * «SETTES INN I ET ARRANGEMENT» — lay a template down on an edition.
 *
 * ADDITIVE, NEVER A REPLACEMENT. Applying a template twice creates the tasks twice, and that is
 * correct rather than a gap: two of them is visible and deletable, whereas a "replace what is
 * there" rule would silently delete work somebody had already started. The alternative — skipping
 * titles that already exist — would quietly refuse to create the second "Ring lydleverandør" that
 * a festival with two stages genuinely needs.
 */
export const applyTaskTemplateRequestSchema = z
  .object({
    editionId: z.uuid(),
    /**
     * Who the created tasks belong to, or nobody.
     *
     * ONE ANSWER FOR THE WHOLE TEMPLATE, and deliberately not a per-item field: the template
     * describes what has to happen, and who does it is a question about this year's crew. Left out,
     * the tasks arrive unassigned, which is a list somebody then distributes — which is what the
     * first hour with a new year's task list is.
     */
    assigneePersonId: z.uuid().nullish(),
  })
  .meta({ id: 'ApplyTaskTemplateRequest' });

/** What an application produced, so the client can say "14 oppgaver ble opprettet". */
export const applyTaskTemplateResponseSchema = z
  .object({
    editionId: z.uuid(),
    created: z.number().int().nonnegative(),
  })
  .meta({ id: 'ApplyTaskTemplateResponse' });

export type TaskTemplateItemDto = z.infer<typeof taskTemplateItemDtoSchema>;
export type TaskTemplateDto = z.infer<typeof taskTemplateDtoSchema>;
export type TaskTemplateListResponse = z.infer<typeof taskTemplateListResponseSchema>;
export type CreateTaskTemplateItem = z.infer<typeof createTaskTemplateItemSchema>;
export type CreateTaskTemplateRequest = z.infer<typeof createTaskTemplateRequestSchema>;
export type CaptureTaskTemplateRequest = z.infer<typeof captureTaskTemplateRequestSchema>;
export type ApplyTaskTemplateRequest = z.infer<typeof applyTaskTemplateRequestSchema>;
export type ApplyTaskTemplateResponse = z.infer<typeof applyTaskTemplateResponseSchema>;

/* ================================================================================================
 * OPPGAVENE GITT MEG FRA ET HUS JEG IKKE ER MED I (sak 216.9, 0395).
 *
 * Byrået gir artisten en oppgave på en booking; artisten er ikke medlem av byrået og leser den
 * gjennom `app.my_given_tasks()`, som svarer for personen selv. En fast, smal form: ingen
 * kommentarer, sjekkliste eller logg — de hører til huset som eier oppgaven.
 * ================================================================================================ */

/** Konserten oppgaven hører til, i formen `/v1/me/concerts` bruker. */
export const givenTaskConcertSchema = z
  .object({
    kind: z.enum(['performance', 'enquiry', 'agency_booking']),
    id: z.uuid(),
  })
  .meta({ id: 'GivenTaskConcert' });

export const givenTaskDtoSchema = z
  .object({
    id: z.uuid(),
    /** Huset oppgaven står i — byrået, eller festivalen på en delt advance. */
    organizationId: z.uuid(),
    organizationName: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    status: taskStatusSchema,
    priority: taskPrioritySchema,
    dueAt: z.iso.datetime().nullable(),
    completedAt: z.iso.datetime().nullable(),
    /** Medansvarlig, ikke ansvarlig. */
    coAssigned: z.boolean(),
    concert: givenTaskConcertSchema.nullable(),
    /**
     * Står her fordi den er DELT (sak 232, T5: på en delt avdeling, programpost eller årgang), ikke
     * gitt. Den åpnes i organisasjonen som delte, som gjest — avkrysningen her gjelder bare de gitte.
     */
    viaShare: z.boolean(),
  })
  .meta({ id: 'GivenTask' });

export const givenTaskListSchema = z
  .object({ items: z.array(givenTaskDtoSchema), ...pageCursorFields })
  .meta({ id: 'GivenTaskList' });

/** Fullfør, eller åpne en fullført igjen. Ingenting annet (0395). */
export const setGivenTaskStatusRequestSchema = z
  .object({ status: z.enum(['done', 'todo']) })
  .meta({ id: 'SetGivenTaskStatusRequest' });

export type GivenTaskConcert = z.infer<typeof givenTaskConcertSchema>;
export type GivenTaskDto = z.infer<typeof givenTaskDtoSchema>;
export type GivenTaskList = z.infer<typeof givenTaskListSchema>;
export type SetGivenTaskStatusRequest = z.infer<typeof setGivenTaskStatusRequestSchema>;
